import { useState, type ReactElement } from 'react';
import type { ChannelDto } from '@gr/core/distribution/channels/dto';
import { PublicationStatus, type PublicationDto } from '@gr/core/distribution/publishing/dto';
import { zonedToUtc } from '@gr/core/time/zoned-time';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { useServices, useT } from '../../../app/di';
import { formatDateTime, fromLocalInputValue, toLocalInputValue } from '../../../shared/format';
import { useAction } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Card } from '../../../ui-kit/Card/Card';
import { useConfirm } from '../../../ui-kit/ConfirmDialog/ConfirmDialog';
import { useBlog } from '../../accounts/workspaces';
import { PublicationHistory } from './PublicationHistory/PublicationHistory';
import { RecordPublicationForm, type RecordValues } from './RecordPublicationForm/RecordPublicationForm';

export interface PublicationHistoryPanelProps {
  postId: string;
  channels: readonly ChannelDto[];
  /** Публикации поста — загружает страница поста одним запросом на обе панели. */
  publications: readonly PublicationDto[];
  /** Выход записан, исправлен или удалён: страница перечитывает публикации и пост. */
  onChanged(): Promise<void>;
}

// PUB-UC-6, PUB-UC-8, PUB-UC-9, PUB-UC-10
export function PublicationHistoryPanel({ postId, channels, publications, onChanged }: PublicationHistoryPanelProps): ReactElement {
  const { api } = useServices();
  const { t, te, locale } = useT();
  const confirm = useConfirm();
  const { blog, can } = useBlog();
  const [recording, setRecording] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const deleting = useAction((publicationId: string) => api.publishing.deletePublished(blog.id, publicationId));
  const items = publications.filter((p) => p.status !== PublicationStatus.Planned);
  const editable = can(BlogPermission.Publish) && channels.length > 0;
  const editingPublication = items.find((p) => p.id === editingId) ?? null;

  const toServer = (values: RecordValues) => ({
    channelId: values.channelId,
    publishedAt: fromLocalInputValue(values.publishedAt, blog.timezone, zonedToUtc) ?? '',
    url: values.url,
  });

  const onDelete = async (publication: PublicationDto): Promise<void> => {
    const at = publication.publishedAt ? formatDateTime(publication.publishedAt, locale, blog.timezone) : '';
    const confirmed = await confirm({
      title: t('publishing.history.deleteConfirm', { channel: publication.channelName, at }),
      text: t('publishing.history.deleteText'),
      confirmLabel: t('common.delete'),
      danger: true,
    });
    if (!confirmed) return;
    if ((await deleting.run(publication.id)).success) {
      if (editingId === publication.id) setEditingId(null);
      await onChanged();
    }
  };

  return (
    <Card
      title={t('publishing.history.title')}
      actions={
        editable ? (
          <Button size="sm" variant="ghost" icon="plus" onClick={() => setRecording(!recording)}>
            {t('publishing.history.record')}
          </Button>
        ) : null
      }
    >
      <div className="u-stack" data-testid="publication-history">
        {deleting.error ? <Alert tone="danger">{te(deleting.error)}</Alert> : null}
        <PublicationHistory
          blogId={blog.id}
          timezone={blog.timezone}
          publications={items}
          onEdit={editable ? (publication) => setEditingId(publication.id) : undefined}
          onDelete={editable ? (publication) => void onDelete(publication) : undefined}
          pending={deleting.pending}
          editing={
            editingPublication
              ? {
                  publicationId: editingPublication.id,
                  form: (
                    <RecordPublicationForm
                      key={editingPublication.id}
                      channels={channels}
                      defaultValues={{
                        channelId: editingPublication.channelId,
                        publishedAt: toLocalInputValue(editingPublication.publishedAt ?? new Date().toISOString(), blog.timezone),
                        url: editingPublication.externalUrl ?? '',
                      }}
                      submitLabel={t('common.save')}
                      onCancel={() => setEditingId(null)}
                      onSubmit={async (values) => {
                        const result = await api.publishing.updatePublished(blog.id, editingPublication.id, toServer(values));
                        if (result.success) {
                          setEditingId(null);
                          await onChanged();
                        }
                        return result;
                      }}
                    />
                  ),
                }
              : null
          }
        />
        {recording ? (
          <RecordPublicationForm
            channels={channels}
            defaultValues={{ channelId: channels[0]?.id ?? '', publishedAt: toLocalInputValue(new Date().toISOString(), blog.timezone), url: '' }}
            submitLabel={t('publishing.history.save')}
            hint={t('publishing.history.recordHint')}
            onSubmit={async (values) => {
              const { url, ...rest } = toServer(values);
              const result = await api.publishing.record(blog.id, postId, { ...rest, url: url || undefined });
              if (result.success) {
                setRecording(false);
                await onChanged();
              }
              return result;
            }}
          />
        ) : null}
      </div>
    </Card>
  );
}
